import { PointerEvent as ReactPointerEvent, useRef } from 'react';

import { Camera, CommentThread } from '@/features/canvas/types';
import { Member } from '@/features/backlog/types';
import { cn } from '@/components/ui/cn';
import { MS, MS_ICON } from './ms';

const DRAG_THRESHOLD = 4; // screen px before a pin press becomes a drag (below it, the press is a click → open)

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : name.slice(0, 2)).toUpperCase();
}

/**
 * The screen-space layer of Miro-like comment pins. Each thread ROOT is drawn at its world anchor
 * (tracking pan/zoom), its pointed bottom-left corner sitting exactly on the point. A press that stays
 * put opens the thread; a press that moves drags the pin to a new anchor (pointer-captured, so it keeps
 * tracking past the marker). Resolved threads render muted with a check; the active one gets a bright ring.
 */
export default function CommentPins({
  cam,
  threads,
  activeId,
  membersById,
  showResolved,
  unread,
  draft,
  onOpen,
  onMove,
  onCommitMove,
}: {
  cam: Camera;
  threads: CommentThread[];
  activeId: string | null;
  membersById: Map<string, Member>;
  showResolved: boolean;
  /** Thread root ids that are new-to-me — pins get an unread dot. */
  unread: Set<string>;
  /** A not-yet-submitted draft pin (comment tool → click) — drawn as a ghost marker. */
  draft?: { x: number; y: number } | null;
  onOpen: (id: string) => void;
  onMove: (id: string, world: { x: number; y: number }) => void;
  onCommitMove: (id: string, world: { x: number; y: number }) => void;
}) {
  const drag = useRef<{ id: string; startX: number; startY: number; moved: boolean } | null>(null);
  const worldOf = (sx: number, sy: number) => ({ x: (sx - cam.x) / cam.zoom, y: (sy - cam.y) / cam.zoom });

  const onPinDown = (e: ReactPointerEvent, id: string) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { id, startX: e.clientX, startY: e.clientY, moved: false };
  };
  const onPinMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_THRESHOLD) return;
    d.moved = true;
    onMove(d.id, worldOf(e.clientX, e.clientY));
  };
  const onPinUp = (e: ReactPointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (d.moved) onCommitMove(d.id, worldOf(e.clientX, e.clientY));
    else onOpen(d.id);
  };

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {draft ? (
        <span
          className="absolute flex h-8 w-8 items-center justify-center rounded-full rounded-bl-none bg-brand text-white shadow-lg ring-2 ring-white"
          style={{ left: draft.x * cam.zoom + cam.x, top: draft.y * cam.zoom + cam.y, transform: 'translate(0, -100%) scale(0.8)', transformOrigin: 'left bottom', zIndex: 3 }}
        >
          <MS icon={MS_ICON.comment} size={15} />
        </span>
      ) : null}
      {threads.map((t) => {
        if (t.root.resolved && !showResolved) return null;
        const px = t.root.x * cam.zoom + cam.x;
        const py = t.root.y * cam.zoom + cam.y;
        const mem = t.root.author_id ? membersById.get(t.root.author_id) : undefined;
        const color = mem?.color || '#2ec5e8';
        const active = activeId === t.root.id;
        const resolved = t.root.resolved;
        return (
          <button
            key={t.root.id}
            type="button"
            onPointerDown={(e) => onPinDown(e, t.root.id)}
            onPointerMove={onPinMove}
            onPointerUp={onPinUp}
            className={cn(
              'pointer-events-auto absolute flex h-8 w-8 cursor-pointer items-center justify-center rounded-full rounded-bl-none text-[11px] font-semibold text-white shadow-lg ring-2 transition-transform hover:scale-110',
              active ? 'ring-white' : 'ring-black/15',
              resolved && !active && 'opacity-60',
            )}
            style={{ left: px, top: py, transform: 'translate(0, -100%) scale(0.8)', transformOrigin: 'left bottom', background: resolved ? '#9aa1ab' : color, zIndex: active ? 2 : 1 }}
            title={resolved ? 'Resolved thread' : 'Open comment thread'}
          >
            {resolved ? <MS icon={MS_ICON.check} size={15} /> : initials(t.root.author_name || mem?.name || '?')}
            {t.count > 1 ? (
              <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[9px] font-bold leading-none text-neutral-900 ring-1 ring-black/15">
                {t.count}
              </span>
            ) : null}
            {unread.has(t.root.id) ? (
              <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-brand ring-2 ring-white" title="New activity" />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
